import React, { useState, useCallback, useEffect } from 'react';
import { Form } from 'react-bootstrap';

interface CardDigitsInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const CardDigitsInput: React.FC<CardDigitsInputProps> = React.memo(({
  value,
  onChange,
  disabled = false
}) => {
  const [error, setError] = useState<string>('');
  // Use local state to avoid re-renders during typing
  const [localValue, setLocalValue] = useState<string>(value);

  // Sync local value when external value changes (e.g., when filters are cleared)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Only allow numeric characters
    const numericValue = inputValue.replace(/[^0-9]/g, '');
    
    // Limit to 4 characters
    const truncatedValue = numericValue.slice(0, 4);
    
    // Clear any error when user is typing
    setError('');
    
    // Update local state immediately (preserves focus)
    setLocalValue(truncatedValue);
  }, []);

  const handleBlur = useCallback(() => {
    // Update parent state on blur
    onChange(localValue);
    
    // Only show error on blur if value is not empty and not exactly 4 digits
    if (localValue.length > 0 && localValue.length !== 4) {
      setError('Must be exactly 4 digits');
    }
  }, [localValue, onChange]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Only allow numeric characters
    if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  }, []);

  return (
    <Form.Group>
      <Form.Label className="text-sm font-medium text-gray-700">
        Card Last 4 Digits
      </Form.Label>
      <Form.Control
        type="text"
        placeholder="e.g., 1234"
        value={localValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyPress}
        disabled={disabled}
        isInvalid={!!error}
        maxLength={4}
        style={{
          fontSize: '16px', // Prevent zoom on iOS
          fontFamily: 'monospace' // Use monospace for better digit alignment
        }}
      />
      {error && (
        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
          {error}
        </Form.Control.Feedback>
      )}
      {!error && (
        <Form.Text className="text-muted">
          Enter exactly 4 digits to filter by payment card
        </Form.Text>
      )}
    </Form.Group>
  );
});

CardDigitsInput.displayName = 'CardDigitsInput';

export default CardDigitsInput;